import { _ } from "../../../../core/polyglot/polyglot.js";
import AbstractColumn from "./AbstractColumn.js";
import percentage from "../../../../core/percentage.js";

/**
 * Deviation report column: Displays deviation(deviated - base) between column values in amount or percentage.
 *
 * @param {abstractColumn} [spec.baseValuesColumn] - column that contains base values.
 * spec.baseValuesColumn is not provided, spec.baseValuesColumnData must be provided to the constructor.
 * @param {abstractColumn} [spec.deviatedValuesColumn] - column that contains deviated values.
 * spec.deviatedValuesColumn is not provided, spec.deviatedValuesColumnData must be provided to the constructor.
 * @param {{}} [spec.baseValuesColumnData] - a basic representation that represents another column in the same report.
 * If spec.baseValuesColumnData is not provided, spec.baseValuesColumn must be provided to the constructor.
 * @param {{}} [spec.deviatedValuesColumnData] - a basic representation that represents another column in the same report.
 * If spec.deviatedValuesColumnData is not provided, spec.deviatedValuesColumn must be provided to the constructor.
 * @param {boolean} spec.inPercent - flag indicating whether deviation is calculated as percentage.
 */
class DeviationColumn extends AbstractColumn {
	constructor({
		baseValuesColumn,
		deviatedValuesColumn,
		baseValuesColumnData,
		deviatedValuesColumnData,
		inPercent = false,
	}) {
		super(...arguments);

		this._baseValuesColumn = baseValuesColumn;
		this._baseValuesColumnData = baseValuesColumnData;
		this._deviatedValuesColumn = deviatedValuesColumn;
		this._deviatedValuesColumnData = deviatedValuesColumnData;
		this._inPercent = inPercent;
	}

	/**
	 *  If _baseValuesColumn doesn't have any assigned value,
	 *  we need to find in the report the column corresponding to the _baseValuesColumnData.
	 *  Possible improvement : All AbstractColumns should have an Id provided by the server.
	 *  We would rely on this Id to retrieve the baseValuesColumn.
	 */
	getBaseValuesColumn() {
		this._baseValuesColumn ||= this._getColumnFromData(
			this._baseValuesColumnData,
		);

		return this._baseValuesColumn;
	}

	/**
	 *  If _deviatedValuesColumn doesn't have any assigned value,
	 *  we need to find in the report the column corresponding to the _deviatedValuesColumnData.
	 *  Possible improvement : All AbstractColumns should have an Id provided by the server.
	 *  We would rely on this Id to retrieve the deviatedValuesColumn.
	 */
	getDeviatedValuesColumn() {
		this._deviatedValuesColumn ||= this._getColumnFromData(
			this._deviatedValuesColumnData,
		);

		return this._deviatedValuesColumn;
	}

	hasPercentageFormat() {
		return this._inPercent;
	}

	getFormatter({ defaultFormatter }) {
		if (this._inPercent) {
			return percentage.forceTwoDecimals;
		}

		return super.getFormatter({ defaultFormatter });
	}

	accept(visitor) {
		return visitor.visitDeviationColumn(this);
	}

	/**
	 * This methods browses the other columns of the report to find one matching the basicColumnData object.
	 * This prevents from handling a copy of each embedded column.
	 */
	_getColumnFromData(basicColumnData) {
		return this.getReport()
			.getColumns()
			.find(
				(column) =>
					basicColumnData &&
					column.getType() === basicColumnData.columnType &&
					(column.getPeriod().toString() === basicColumnData.period ||
						column.getPeriod().toSmartString().toString() ===
							basicColumnData.period),
			);
	}

	_getDefaultTitle() {
		if (this._inPercent) {
			return _("Deviation in %");
		}
		return _("Deviation");
	}

	_serializedProperties() {
		const properties = super._serializedProperties();

		properties.baseValuesColumn = this._baseValuesColumn;
		properties.deviatedValuesColumn = this._deviatedValuesColumn;
		properties.inPercent = this._inPercent;

		return properties;
	}

	static getType() {
		return "DeviationColumn";
	}

	/**
	 * We swap baseValuesColumn to baseValuesColumnData and
	 * deviatedValuesColumn to deviatedValuesColumnData.
	 */
	static transformMaterializationData(data) {
		super.transformMaterializationData(...arguments);

		data.baseValuesColumnData = data.baseValuesColumn;
		data.deviatedValuesColumnData = data.deviatedValuesColumn;

		data.baseValuesColumn = undefined;
		data.deviatedValuesColumn = undefined;
	}
}

export default DeviationColumn;
